import React, { useState } from "react";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import { FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import { transparentize } from "polished";

export const Container = styled.div<SpaceProps>`
  ${space};
  max-width: 100%;
  position: relative;
`;

Container.defaultProps = {
  mb: 3,
};

export const Label = styled.label`
  display: block;
  font-size: ${(props) => props.theme.fontSizes[2]}px;
  font-weight: 600;
  margin-bottom: 3px;
`;

export const Input = styled.input<SpaceProps>`
  border: 1px solid rgba(128, 128, 128, 0.8);
  border-radius: 3px;
  background-color: ${(props) => props.theme.colors.background};
  width: 100%;
  display: block;
  padding: 6px;
  color: ${(props) => props.theme.colors.body};

  &:focus,
  &:active {
    outline: 0;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: inset 0 0 3px
      ${(props) => transparentize(0.8, props.theme.colors.primary)};
  }

  &::placeholder {
    font-size: 12px;
    color: #aaa;
  }

  ${space};
`;

export const ErrorText = styled.p`
  color: ${(props) => props.theme.colors.danger};
  font-size: 12px;
  margin-top: 3px;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0;
  top: 32px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  color: ${(props) => props.theme.colors.body};
  cursor: pointer;
  background: none;
  border: 0;
  font-size: 0.8em;

  &:hover {
    opacity: 1;
  }
`;

const defaultMessages = {
  required: "Field is required",
  minLength: "Field is too short",
};

type FormInputProps = {
  label?: string;
  name?: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  disabled?: boolean;
  type?: string;
  error?: any;
  placeholder?: string;
  tabindex?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onClear?: () => void;
  renderLabel?: ({
    name,
    label,
  }: {
    name?: string;
    label?: string;
  }) => React.ReactNode;
} & SpaceProps;

export const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      name,
      onChange,
      value,
      disabled,
      type,
      error,
      placeholder,
      tabindex,
      autoComplete,
      autoFocus,
      onClear,
      onFocus,
      onBlur,
      renderLabel = (props) => (
        <Label htmlFor={props.name}>{props.label}</Label>
      ),
      ...rest
    },
    ref
  ) => {
    const [showPass, setShowPass] = useState<boolean>(false);

    return (
      <Container {...rest}>
        {renderLabel({ label, name })}
        <Input
          autoFocus={autoFocus}
          tabIndex={tabindex}
          name={name}
          id={name}
          type={type === "password" && showPass ? "text" : type}
          onChange={onChange}
          disabled={disabled}
          ref={ref}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {type === "password" && (
          <ClearButton
            onClick={() => setShowPass(!showPass)}
            type="button"
            tabIndex={-1}
            style={{ right: 32 }}
          >
            {!showPass ? <FaEye /> : <FaEyeSlash />}
          </ClearButton>
        )}
        {onClear && (
          <ClearButton onClick={onClear} type="button" tabIndex={-1}>
            <FaTimes />
          </ClearButton>
        )}
        {error && (
          <ErrorText>
            {error.message || defaultMessages[error.type] || "Field is invalid"}
          </ErrorText>
        )}
      </Container>
    );
  }
);
